import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatBRL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CarCard } from "@/components/CarCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { CAMBIOS } from "@/lib/carOptions";
import { Search, SlidersHorizontal, X, BellPlus, TrendingDown, Check, RotateCcw } from "lucide-react";

const FILTER_OPCIONAIS = ["Ar-condicionado", "Banco em couro", "Teto solar", "Central multimídia", "Câmera de ré", "Rodas de liga leve", "Freio ABS", "Airbag"];

export default function Buscar() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cars, setCars] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertName, setAlertName] = useState("");

  const savedFilters = (() => { try { return JSON.parse(localStorage.getItem("piccar_filters") || "null"); } catch { return null; } })();
  const hasUrl = ["q", "marca", "estado", "cidade", "ano_min", "preco_max", "cambio", "sort"].some((k) => params.get(k) != null);
  const sf = hasUrl ? null : savedFilters;

  const [q, setQ] = useState(params.get("q") || sf?.q || "");
  const [marca, setMarca] = useState(params.get("marca") || sf?.marca || "todas");
  const [estado, setEstado] = useState(params.get("estado") || sf?.estado || "todos");
  const [cidade, setCidade] = useState(params.get("cidade") || sf?.cidade || "todas");
  const [locations, setLocations] = useState({ estados: [], cidades: {} });
  const [anoMin, setAnoMin] = useState(params.get("ano_min") || sf?.anoMin || "");
  const [precoMax, setPrecoMax] = useState([params.get("preco_max") ? Number(params.get("preco_max")) : (sf?.precoMax ?? 500000)]);
  const [cambio, setCambio] = useState(params.get("cambio") || sf?.cambio || "todos");
  const [feats, setFeats] = useState(sf?.feats || []);
  const [abaixoFipe, setAbaixoFipe] = useState(sf?.abaixoFipe || false);
  const [sort, setSort] = useState(params.get("sort") || sf?.sort || "recentes");
  const [restored, setRestored] = useState(!!sf);

  useEffect(() => { api.get("/brands").then(({ data }) => setBrands(data)); }, []);
  useEffect(() => { api.get("/locations").then(({ data }) => setLocations(data)); }, []);

  const cidadesDoEstado = estado !== "todos" ? (locations.cidades[estado] || []) : [];
  const onEstadoChange = (v) => { setEstado(v); setCidade("todas"); };

  const toggleFeat = (opt) => setFeats((s) => s.includes(opt) ? s.filter((x) => x !== opt) : [...s, opt]);

  const load = useCallback(async () => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (marca && marca !== "todas") p.marca = marca;
    if (estado && estado !== "todos") p.estado = estado;
    if (cidade && cidade !== "todas") p.cidade = cidade;
    if (anoMin) p.ano_min = anoMin;
    if (precoMax[0] < 500000) p.preco_max = precoMax[0];
    if (cambio && cambio !== "todos") p.cambio = cambio;
    if (feats.length) p.features = feats.join(",");
    if (abaixoFipe) p.abaixo_fipe = true;
    if (sort && sort !== "recentes") p.sort = sort;
    // lembra os últimos filtros do comprador
    localStorage.setItem("piccar_filters", JSON.stringify({ q, marca, estado, cidade, anoMin, precoMax: precoMax[0], cambio, feats, abaixoFipe, sort }));
    try {
      const { data } = await api.get("/listings", { params: p });
      setCars(data);
    } finally { setLoading(false); }
  }, [q, marca, estado, cidade, anoMin, precoMax, cambio, feats, abaixoFipe, sort]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort]);

  // Restaurar posição da lista ao voltar da página de um carro.
  // Lê+consome o sessionStorage só no momento de restaurar (evita corrida com StrictMode).
  const didRestore = useRef(false);
  useEffect(() => {
    if (loading || didRestore.current) return;
    didRestore.current = true;
    let y = 0;
    const saved = sessionStorage.getItem("buscarScroll");
    sessionStorage.removeItem("buscarScroll");
    if (saved) {
      try { const o = JSON.parse(saved); if (Date.now() - o.t < 600000) y = o.y; } catch {}
    }
    requestAnimationFrame(() => window.scrollTo(0, y));
  }, [loading]);
  const rememberScroll = () => sessionStorage.setItem("buscarScroll", JSON.stringify({ y: window.scrollY, t: Date.now() }));

  const apply = () => { setParams(q ? { q } : {}); load(); };
  const clear = () => { setQ(""); setMarca("todas"); setEstado("todos"); setCidade("todas"); setAnoMin(""); setPrecoMax([500000]); setCambio("todos"); setFeats([]); setAbaixoFipe(false); setSort("recentes"); setParams({}); setRestored(false); localStorage.removeItem("piccar_filters"); setTimeout(load, 0); };

  const currentFilters = () => {
    const f = {};
    if (q) f.q = q;
    if (marca && marca !== "todas") f.marca = marca;
    if (anoMin) f.ano_min = parseInt(anoMin);
    if (precoMax[0] < 500000) f.preco_max = precoMax[0];
    return f;
  };

  const openAlert = () => {
    if (!user) { toast.info("Entre para salvar alertas de carro."); navigate("/entrar", { state: { from: "/buscar" } }); return; }
    setAlertName("");
    setAlertOpen(true);
  };

  const saveAlert = async () => {
    try {
      await api.post("/alerts", { name: alertName, ...currentFilters() });
      toast.success("Alerta criado! Avisaremos quando entrar um carro novo.");
      setAlertOpen(false);
    } catch { toast.error("Erro ao criar alerta"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="buscar-page">
      <p className="overline text-blaze mb-2">Vitrine PicCar</p>
      <h1 className="font-heading font-800 text-3xl md:text-4xl mb-4">Buscar Carros</h1>

      {restored && (q || marca !== "todas" || estado !== "todos" || anoMin || precoMax[0] < 500000 || cambio !== "todos" || feats.length || abaixoFipe || sort !== "recentes") && (
        <div className="mb-6 inline-flex items-center gap-3 bg-blaze/5 border border-blaze/20 rounded-sm px-3 py-2" data-testid="filters-restored-note">
          <RotateCcw className="w-4 h-4 text-blaze shrink-0" />
          <span className="text-sm font-600 text-obsidian">Restauramos os filtros da sua última busca.</span>
          <button onClick={clear} className="text-sm font-700 text-blaze hover:underline" data-testid="filters-restored-clear">Limpar</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* FILTERS */}
        <aside className="lg:col-span-3">
          <div className="bg-white border border-border rounded-sm p-6 sticky top-24">
            <div className="flex items-center gap-2 mb-6">
              <SlidersHorizontal className="w-4 h-4 text-blaze" />
              <span className="font-700 text-sm">Filtros</span>
            </div>

            <label className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Buscar</label>
            <div className="relative mt-2 mb-5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Marca ou modelo" className="pl-9 rounded-sm" data-testid="filter-q" />
            </div>

            <label className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Marca</label>
            <Select value={marca} onValueChange={setMarca}>
              <SelectTrigger className="mt-2 mb-5 rounded-sm" data-testid="filter-marca"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as marcas</SelectItem>
                {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>

            <label className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Estado</label>
            <Select value={estado} onValueChange={onEstadoChange}>
              <SelectTrigger className="mt-2 mb-5 rounded-sm" data-testid="filter-estado"><SelectValue placeholder="Todos os estados" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os estados</SelectItem>
                {locations.estados.map((uf) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
              </SelectContent>
            </Select>

            <label className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Cidade</label>
            <Select value={cidade} onValueChange={setCidade} disabled={estado === "todos" || cidadesDoEstado.length === 0}>
              <SelectTrigger className="mt-2 mb-5 rounded-sm" data-testid="filter-cidade"><SelectValue placeholder={estado === "todos" ? "Escolha um estado" : "Todas as cidades"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as cidades</SelectItem>
                {cidadesDoEstado.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <label className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Ano a partir de</label>
            <Input value={anoMin} onChange={(e) => setAnoMin(e.target.value.replace(/\D/g, ""))} placeholder="Ex: 2018" className="mt-2 mb-5 rounded-sm" data-testid="filter-ano" />

            <label className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Preço até {formatBRL(precoMax[0])}</label>
            <Slider min={10000} max={500000} step={5000} value={precoMax} onValueChange={setPrecoMax} className="mt-4 mb-6" data-testid="filter-preco" />

            <label className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Câmbio</label>
            <Select value={cambio} onValueChange={setCambio}>
              <SelectTrigger className="mt-2 mb-5 rounded-sm" data-testid="filter-cambio"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {CAMBIOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <label className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Opcionais</label>
            <div className="mt-2 mb-5 flex flex-wrap gap-1.5">
              {FILTER_OPCIONAIS.map((opt) => {
                const on = feats.includes(opt);
                return (
                  <button key={opt} type="button" onClick={() => toggleFeat(opt)} data-testid={`filter-opcional-${opt}`}
                    className={`inline-flex items-center gap-1 text-[11px] font-600 px-2 py-1 rounded-sm border transition-colors ${on ? "border-blaze bg-blaze/5 text-obsidian" : "border-border text-muted-foreground hover:border-obsidian"}`}>
                    {on && <Check className="w-3 h-3 text-blaze" strokeWidth={3} />}{opt}
                  </button>
                );
              })}
            </div>

            <label className="flex items-center gap-2 mb-6 cursor-pointer" data-testid="filter-abaixo-fipe-wrap">
              <Checkbox checked={abaixoFipe} onCheckedChange={(v) => setAbaixoFipe(!!v)} data-testid="filter-abaixo-fipe" />
              <span className="text-sm font-600 flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5 text-emerald-600" /> Só abaixo da FIPE</span>
            </label>

            <Button className="w-full bg-obsidian hover:bg-blaze text-white rounded-sm font-700" onClick={apply} data-testid="filter-apply">Aplicar Filtros</Button>
            <Button variant="outline" className="w-full mt-2 rounded-sm border-blaze/40 text-blaze hover:bg-blaze hover:text-white font-600" onClick={openAlert} data-testid="criar-alerta-btn"><BellPlus className="w-4 h-4 mr-1" /> Criar alerta desta busca</Button>
            <Button variant="ghost" className="w-full mt-1 text-muted-foreground" onClick={clear} data-testid="filter-clear"><X className="w-4 h-4 mr-1" /> Limpar</Button>
          </div>
        </aside>

        {/* RESULTS */}
        <div className="lg:col-span-9">
          <div className="flex items-center justify-between gap-3 mb-5">
            <p className="text-sm text-muted-foreground" data-testid="results-count">
              {loading ? "Carregando..." : `${cars.length} carro(s) encontrado(s)`}
            </p>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-56 rounded-sm h-9" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recentes">Mais recentes</SelectItem>
                <SelectItem value="fipe_desc">Maior desconto FIPE</SelectItem>
                <SelectItem value="preco_asc">Menor preço</SelectItem>
                <SelectItem value="preco_desc">Maior preço</SelectItem>
                <SelectItem value="km_asc">Menor quilometragem</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!loading && cars.length === 0 ? (
            <div className="bg-white border border-border rounded-sm p-16 text-center text-muted-foreground">
              Nenhum carro encontrado com esses filtros.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" onClickCapture={rememberScroll} data-testid="results-grid">
              {cars.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
            </div>
          )}
        </div>
      </div>

      <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
        <DialogContent data-testid="alert-dialog">
          <DialogHeader>
            <DialogTitle>Criar alerta de carro</DialogTitle>
            <DialogDescription>Salvaremos os filtros atuais e avisaremos você quando entrar um carro novo que combine.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>Nome do alerta (opcional)</Label>
            <Input value={alertName} onChange={(e) => setAlertName(e.target.value)} placeholder="Ex: Civic até 130 mil" className="mt-2 rounded-sm" data-testid="alert-name-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAlertOpen(false)}>Cancelar</Button>
            <Button className="bg-obsidian hover:bg-blaze text-white rounded-sm" onClick={saveAlert} data-testid="alert-save-btn">Criar alerta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
